import * as _ from 'lodash';

import { QuestionID, QuestionSummary } from '@nexxt/common/types';

/**
 * Returns list of childrens from current group question
 * @param {number} questionId - questionId of current group
 * @param {QuestionSummary} sortableQuestionObject - object format of current sortable question
 * @returns {QuestionSummary[]}
 */
export function getChildQuestionFromSortableQuestion(
    questionId: QuestionID,
    sortableQuestionObject: { [key: number]: QuestionSummary }
): QuestionSummary[] {
    return _.cloneDeep(
        (sortableQuestionObject &&
            sortableQuestionObject[questionId] &&
            sortableQuestionObject[questionId].childrenSummaries) ||
            []
    );
}

/**
 * Returns flow of question
 * @param {number} questionId - questionId of current group
 * @param {QuestionSummary} sortableQuestionObject - object format of current sortable question
 * @param {QuestionSummary[]} sortableQuestions - list of all sortable questions
 * @returns {QuestionSummary[]}
 */
export function getAvailableChildrenQuestions(
    questionId: QuestionID,
    sortableQuestionObject: { [key: number]: QuestionSummary },
    sortableQuestions: QuestionSummary[]
): QuestionSummary[] {
    const currentQuestion =
        (sortableQuestionObject && sortableQuestionObject[questionId]) || null;
    let availableQuestions: QuestionSummary[];

    if (!currentQuestion || !currentQuestion.parent) {
        availableQuestions = sortableQuestions.filter(
            (question: QuestionSummary) => question.id !== questionId
        );
    } else {
        const parentQuestion = sortableQuestionObject[currentQuestion.parent];

        availableQuestions = (parentQuestion.childrenSummaries || []).filter(
            (child: QuestionSummary) => child.id !== questionId
        );
    }
    availableQuestions = availableQuestions.filter(
        (q) => !(q.isAudience && q.builtInID)
    );

    const formattedAvailableQuestions: QuestionSummary[] =
        availableQuestions.map((question: QuestionSummary) => ({
            id: question.id,
            childrenSummaries: question.childrenSummaries,
            childrenIds: question.childrenIds,
            title: question.title || question.label,
            isGroup: question.isGroup,
            label: question.label || question['title'],
            position: question.position,
            parent: question.parent,
            questionTexts: question.questionTexts,
            type: question.type,
            value: question.value || String(question.id),
            isPiping: question.isPiping
        }));

    if (questionId && currentQuestion) {
        return [
            ...(currentQuestion.childrenSummaries || []),
            ...availableQuestions
        ];
    }
    return availableQuestions;
}

/**
 * Build flow object to save flow of current question orders
 * @param {number} questionId
 * @param {QuestionSummary[]} selectedQuestions
 * @param {QuestionSummary[]} sortableQuestions
 * @param {{questionId: number}QuestionSummary[]}  sortableQuestionObject
 * @returns {QuestionSummary[]}
 */
export function getUpdatedFlow(
    questionId: QuestionID,
    selectedQuestions: QuestionSummary[],
    sortableQuestions: QuestionSummary[],
    sortableQuestionObject: { [questionId: number]: QuestionSummary }
): QuestionSummary[] {
    const _selectedQuestion = _.cloneDeep(selectedQuestions);
    const _sortableQuestions = _.cloneDeep(sortableQuestions);
    const _sortableQuestionObject = _.cloneDeep(sortableQuestionObject);

    const currentQuestion =
        (questionId && _sortableQuestionObject[questionId]) || null;

    if (questionId && currentQuestion) {
        if (!currentQuestion.parent) {
            return sortQuestionFlow(
                getFlowFromRootQuestion(
                    questionId,
                    _selectedQuestion,
                    _sortableQuestions
                )
            );
        } else {
            const parentQuestion =
                _sortableQuestionObject[currentQuestion.parent];
            return sortQuestionFlow(
                getFlowFromParentQuestion(
                    questionId,
                    _selectedQuestion,
                    parentQuestion,
                    _sortableQuestions
                )
            );
        }
    }
    return sortQuestionFlow(
        getFlowFromRootQuestion(
            questionId,
            _selectedQuestion,
            _sortableQuestions
        )
    );
}

/**
 * Return flow with updated position number. It is a recursive function.
 * @param {QuestionSummary[]} questions
 * @returns {QuestionSummary}
 */
function sortQuestionFlow(questions: QuestionSummary[]): QuestionSummary[] {
    return questions.map((question: QuestionSummary, index) => {
        if (question.childrenSummaries && question.childrenSummaries.length) {
            const childrenSummaries = sortQuestionFlow(
                question.childrenSummaries
            );
            return {
                ...question,
                position: index,
                childrenSummaries,
                childrenIds: childrenSummaries.map((child) => child.id)
            };
        } else {
            return { ...question, position: index };
        }
    });
}

/**
 *
 * Returns new flow of question which has a parent as root question(parent = 0)
 *
 * @param questionId - questionId of current group
 * @param selectedQuestions - new children questions for this group
 * @param sortableQuestions  - list of all sortable questions
 */
function getFlowFromRootQuestion(
    questionId: QuestionID,
    selectedQuestions: QuestionSummary[],
    sortableQuestions: QuestionSummary[]
): QuestionSummary[] {
    const selectedQuestionIds = selectedQuestions.map((q) => q.id);
    const currentQuestion = sortableQuestions.filter(
        (filter) => filter.id === questionId
    )[0];

    const prevChildrenIds = currentQuestion.childrenIds;
    const removedChildrenIds = prevChildrenIds.filter(
        (filter) => !selectedQuestionIds.includes(filter)
    );

    let removedChildrenObjects = currentQuestion.childrenSummaries.filter(
        (filter) => removedChildrenIds.includes(filter.id)
    );

    let questions = sortableQuestions || [];

    if (removedChildrenIds.length) {
        // re-assign parent id as a root
        removedChildrenObjects = removedChildrenObjects.map((rc) => ({
            ...rc,
            parent: 0
        }));
        questions = questions.concat(removedChildrenObjects);
    }
    const filteredQuestions = questions.filter(
        (filter) => !selectedQuestionIds.includes(filter.id)
    );

    return filteredQuestions.map((question) => {
        if (question.id === questionId) {
            // updating group's children list
            const children = _.cloneDeep(selectedQuestions);
            return {
                ...question,
                childrenSummaries: children.map((c) => ({
                    ...c,
                    parent: questionId
                })),
                childrenIds: children.map((c) => c.id)
            };
        }
        return question;
    });
}

/**
 * Returns flow of question which has parent a question
 * @param questionId - questionId of current group
 * @param selectedQuestions - new children questions for this group
 * @param parentQuestion - current group question object
 * @param sortableQuestions - list of all sortable questions
 */
function getFlowFromParentQuestion(
    questionId: QuestionID,
    selectedQuestions: QuestionSummary[],
    parentQuestion: QuestionSummary,
    sortableQuestions: QuestionSummary[]
): QuestionSummary[] {
    // selected IDs of current group
    const selectedQuestionIds = selectedQuestions.map((q) => q.id);
    const sortableQuestionIds = sortableQuestions.map((q) => q.id);

    return sortableQuestions.map((sortableQuestion) => {
        // if parentQuestion id exists on the sortableQuestions
        if (sortableQuestionIds.includes(parentQuestion.id)) {
            let removedChildrenObject = [];

            // Updates parent's question list
            const children = (sortableQuestion.childrenSummaries || [])
                .filter(
                    (filterChild) =>
                        !selectedQuestionIds.includes(filterChild.id)
                )
                .map((child) => {
                    if (child.id === questionId) {
                        // Find the removed children questions
                        removedChildrenObject = child.childrenSummaries.filter(
                            (removedChild) =>
                                !selectedQuestionIds.includes(removedChild.id)
                        );

                        // Updates group's children list
                        const newChildren = _.cloneDeep(selectedQuestions).map(
                            (newChild) => ({
                                ...newChild,
                                parent: child.id
                            })
                        );

                        return {
                            ...child,
                            childrenSummaries: newChildren,
                            childrenIds: newChildren.map((child) => child.id)
                        };
                    }
                    return child;
                });

            // re-assign parent ID to the removed children questions
            if (removedChildrenObject.length) {
                removedChildrenObject = removedChildrenObject.map((child) => ({
                    ...child,
                    parent: sortableQuestion.id
                }));
            }
            const childrenSummaries = children.concat(removedChildrenObject);
            return {
                ...sortableQuestion,
                childrenSummaries,
                childrenIds: childrenSummaries.map((child) => child.id)
            };
        } else {
            const childrenSummaries = getFlowFromParentQuestion(
                questionId,
                selectedQuestions,
                parentQuestion,
                sortableQuestion.childrenSummaries
            );
            sortableQuestion.childrenSummaries = childrenSummaries;
            sortableQuestion.childrenIds = childrenSummaries.map(
                (child) => child.id
            );
        }
        return sortableQuestion;
    });
}
